import * as React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

import RefreshIcon from '@mui/icons-material/Refresh';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts';

import AuthService from '../services/auth';
import AppConfig from '../services/app-config';
import apiClient, { shouldRedirectToLogin } from '../services/api-client';
import {
  rangeToParams,
  toVisitsSeries,
  toVisitsHourlySeries,
  toReservationsSeries,
  toIssuesSeries,
  bucketMonthly,
  bucketIssuesMonthly,
  toDaytimeBar,
  toCategoryBar,
  toAmenityBar,
  chartTooltipProps,
  chartAxisProps,
} from '../utils/reporteriaTransforms';

const RANGE_OPTIONS = [
  { value: '24h', label: '24 horas' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: '6m', label: '6 meses' },
];

function formatCurrency(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return 'Q. 0';
  return 'Q. ' + num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatInt(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return Math.round(num).toLocaleString('es-MX');
}

function ChartCard({ title, children, sx }) {
  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: 0,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        height: '100%',
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: 'text.primary' }}>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <Box
      sx={{
        height: 280,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography color="text.secondary" variant="body2">
        Sin datos en este período
      </Typography>
    </Box>
  );
}

function ChartSkeleton() {
  return <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />;
}

function KpiCard({ label, value, subtitle, loading }) {
  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: 0,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        {loading ? (
          <Skeleton variant="text" height={32} width="60%" />
        ) : (
          <Typography variant="h5" fontWeight={800} sx={{ color: 'text.primary', lineHeight: 1.1 }}>
            {value}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', opacity: 0.7 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function Reporteria({ setNavbarState, navbarState }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [range, setRange] = React.useState('week');
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '' });

  React.useEffect(() => {
    setNavbarState({ ...navbarState, showBack: true, showOptions: false, showSearch: false });
  }, []);

  const fetchStats = React.useCallback(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setStats(null);

    const { start, end, mode } = rangeToParams(range);
    const org = AuthService.getCurrentUserOrgKey();

    apiClient
      .get(`internal/stats/${org}/${mode}/${start}/${end}`, {}, { action: 'fetch_reporteria_stats' })
      .then((response) => {
        if (!active) return;
        if (response && response.status === 200) {
          setStats(response.data && response.data.data ? response.data.data : null);
        } else if (response && response.status === 401) {
          setRedirect({ trigger: true, route: '/login' });
        } else {
          setError('No se pudieron cargar las métricas');
        }
      })
      .catch((err) => {
        if (!active) return;
        if (shouldRedirectToLogin(err)) {
          setRedirect({ trigger: true, route: '/login' });
        } else {
          setError('Error de conexión');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [range]);

  React.useEffect(() => {
    const cleanup = fetchStats();
    return cleanup;
  }, [fetchStats]);

  const visits = stats && stats.visits ? stats.visits : {};
  const reservations = stats && stats.reservations ? stats.reservations : {};
  const issues = stats && stats.issues ? stats.issues : {};

  const computed = React.useMemo(() => {
    const is24h = range === '24h';
    const isLongRange = range === '6m';

    let visitsSeries;
    if (is24h) {
      visitsSeries = toVisitsHourlySeries(visits);
    } else {
      const visitsDaily = toVisitsSeries(visits);
      visitsSeries = isLongRange ? bucketMonthly(visitsDaily) : visitsDaily;
    }

    const reservationsDaily = toReservationsSeries(reservations);
    const reservationsSeries = isLongRange ? bucketMonthly(reservationsDaily) : reservationsDaily;

    const issuesDaily = toIssuesSeries(issues);
    const issuesSeries = isLongRange ? bucketIssuesMonthly(issuesDaily) : issuesDaily;

    return {
      visitsSeries,
      reservationsSeries,
      issuesSeries,
      daytimeBar: toDaytimeBar(visits),
      categoryBar: toCategoryBar(issues),
      amenityBar: toAmenityBar(reservations),
    };
  }, [stats, range, theme.palette.mode]);

  if (!AuthService.userRoleIncludes(['admin']) || !AppConfig.haveModule('reporting')) {
    return <Navigate to="/" replace={true} />;
  }

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace={true} />;
  }

  const totalVisits = safeTotal(visits.total_visits);
  const totalReservations = safeTotal(reservations.total_reservations);
  const estimatedChargesTotal = safeTotal(reservations.estimated_charges_total);
  const totalIssues = safeTotal(issues.total_issues);
  const totalActiveIssues = safeTotal(issues.total_active_issues);
  const totalResolvedIssues = safeTotal(issues.total_resolved_issues);
  const resolutionRate = totalIssues > 0 ? Math.round((totalResolvedIssues / totalIssues) * 100) : 0;

  const axisProps = chartAxisProps(theme);
  const tooltipProps = chartTooltipProps(theme);

  const handleRangeChange = (_, v) => {
    if (!v) return;
    setRange(v);
    setLoading(true);
    setStats(null);
    setError(null);
  };

  const amenityTooltipFormatter = (value, name, props) => {
    const cargos = props && props.payload && typeof props.payload.cargos === 'number' ? props.payload.cargos : null;
    if (cargos != null && cargos > 0) {
      return [`${formatInt(value)} · ${formatCurrency(cargos)}`, 'Reservas'];
    }
    return [formatInt(value), 'Reservas'];
  };

  return (
    <Container maxWidth="md" disableGutters>
      <Box
        sx={{
          bgcolor: 'background.default',
          minHeight: '100vh',
          pb: 'calc(80px + env(safe-area-inset-bottom))',
          transition: 'background-color 200ms ease',
        }}
      >
        {/* Sticky header */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            bgcolor: 'background.default',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 1,
            px: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                width: 40,
                height: 40,
                ml: -1,
                color: 'text.primary',
                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08) },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 28 }} />
            </IconButton>
            <AssessmentIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={800} sx={{ color: 'text.primary' }}>
              Reportería
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflowX: 'auto' }}>
            <ToggleButtonGroup
              value={range}
              exclusive
              size="small"
              onChange={handleRangeChange}
              sx={{
                '& .MuiToggleButton-root': {
                  px: 1.5,
                  py: 0.25,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                },
              }}
            >
              {RANGE_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" onClick={fetchStats} aria-label="Reintentar" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={fetchStats}>
                  Reintentar
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {/* KPI row */}
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <KpiCard label="Visitas" value={formatInt(totalVisits)} loading={loading} />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiCard
                label="Reservas"
                value={formatInt(totalReservations)}
                subtitle={formatCurrency(estimatedChargesTotal)}
                loading={loading}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiCard
                label="Reportes"
                value={formatInt(totalIssues)}
                subtitle={`${formatInt(totalActiveIssues)} activos`}
                loading={loading}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiCard label="Resueltos" value={`${resolutionRate}%`} loading={loading} />
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={2}>
            {/* 1. Visitas en el tiempo */}
            <Grid item xs={12}>
              <ChartCard title="Visitas en el tiempo">
                {loading ? (
                  <ChartSkeleton />
                ) : computed.visitsSeries.length === 0 ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={computed.visitsSeries} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                      <defs>
                        <linearGradient id="visitsGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" {...axisProps} />
                      <YAxis {...axisProps} allowDecimals={false} />
                      <Tooltip {...tooltipProps} />
                      <Area
                        type="monotone"
                        dataKey="value"
                        name="Visitas"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                        fill="url(#visitsGrad)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>

            {/* 2. Reportes vs Resueltos */}
            <Grid item xs={12} md={6}>
              <ChartCard title="Reportes vs Resueltos">
                {loading ? (
                  <ChartSkeleton />
                ) : computed.issuesSeries.length === 0 ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={computed.issuesSeries} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                      <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" {...axisProps} />
                      <YAxis {...axisProps} allowDecimals={false} />
                      <Tooltip {...tooltipProps} />
                      <Legend />
                      <Bar dataKey="reportados" name="Reportados" fill={theme.palette.error.main} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="resueltos" name="Resueltos" fill={theme.palette.secondary.main} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>

            {/* 3. Reservas en el tiempo */}
            <Grid item xs={12} md={6}>
              <ChartCard title="Reservas en el tiempo">
                {loading ? (
                  <ChartSkeleton />
                ) : computed.reservationsSeries.length === 0 ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={computed.reservationsSeries} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                      <defs>
                        <linearGradient id="reservationsGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={theme.palette.info.main} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={theme.palette.info.main} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" {...axisProps} />
                      <YAxis {...axisProps} allowDecimals={false} />
                      <Tooltip {...tooltipProps} />
                      <Area
                        type="monotone"
                        dataKey="value"
                        name="Reservas"
                        stroke={theme.palette.info.main}
                        strokeWidth={2}
                        fill="url(#reservationsGrad)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>

            {/* 4. Visitas por horario */}
            <Grid item xs={12} md={6}>
              <ChartCard title="Visitas por horario">
                {loading ? (
                  <ChartSkeleton />
                ) : computed.daytimeBar.length === 0 ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={computed.daytimeBar}
                      layout="vertical"
                      margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                    >
                      <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" {...axisProps} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={80} {...axisProps} />
                      <Tooltip {...tooltipProps} />
                      <Bar dataKey="value" name="Visitas" fill={theme.palette.primary.main} radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>

            {/* 5. Reportes por categoría */}
            <Grid item xs={12} md={6}>
              <ChartCard title="Reportes por categoría">
                {loading ? (
                  <ChartSkeleton />
                ) : computed.categoryBar.length === 0 ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={computed.categoryBar}
                      layout="vertical"
                      margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                    >
                      <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" {...axisProps} allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={110}
                        {...axisProps}
                        tick={{ ...axisProps.tick, width: 100 }}
                      />
                      <Tooltip {...tooltipProps} />
                      <Bar dataKey="value" name="Reportes" fill={theme.palette.error.main} radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>

            {/* 6. Reservas por amenidad */}
            <Grid item xs={12}>
              <ChartCard title="Reservas por amenidad">
                {loading ? (
                  <ChartSkeleton />
                ) : computed.amenityBar.length === 0 ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={computed.amenityBar}
                      layout="vertical"
                      margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                    >
                      <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" {...axisProps} allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={110}
                        {...axisProps}
                        tick={{ ...axisProps.tick, width: 100 }}
                      />
                      <Tooltip {...tooltipProps} formatter={amenityTooltipFormatter} />
                      <Bar dataKey="value" name="Reservas" fill={theme.palette.info.main} radius={[0, 3, 3, 0]}>
                        {computed.amenityBar.map((entry, idx) => (
                          <Cell key={`amenity-cell-${idx}`} fill={theme.palette.info.main} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}

function safeTotal(v) {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}
